import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { DialogProvider } from './hooks/useDialog'
import MainLayout from './components/layout/MainLayout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import UserList from './pages/users/UserList'
import ClientList from './pages/clients/ClientList'
import ClientForm from './pages/clients/ClientForm'
import ClientDetail from './pages/clients/ClientDetail'
import PropertyList from './pages/properties/PropertyList'
import PropertyForm from './pages/properties/PropertyForm'
import PropertyDetail from './pages/properties/PropertyDetail'
import ContractList from './pages/contracts/ContractList'
import ContractForm from './pages/contracts/ContractForm'
import ContractDetail from './pages/contracts/ContractDetail'
import PaymentList from './pages/payments/PaymentList'
import PaymentDetail from './pages/payments/PaymentDetail'
import ReciboList from './pages/recibos/ReciboList'
import ReciboCreate from './pages/recibos/ReciboCreate'
import ReciboDetail from './pages/recibos/ReciboDetail'
import RecentRecibos from './pages/recibos/RecentRecibos'
import ReportsDashboard from './pages/reports/ReportsDashboard'
import OverduePaymentsReport from './pages/reports/OverduePaymentsReport'
import AdjustmentContractsReport from './pages/reports/AdjustmentContractsReport'
import PropertiesDeliveredReport from './pages/reports/PropertiesDeliveredReport'
import ReceiptsReport from './pages/reports/ReceiptsReport'
import LegalListReport from './pages/reports/LegalListReport'
import ContractsByDateReport from './pages/reports/ContractsByDateReport'
import ContractsByMonthReport from './pages/reports/ContractsByMonthReport'
import CancelledIssuedReport from './pages/reports/CancelledIssuedReport'
import ClientsReport from './pages/reports/ClientsReport'
import PropertiesReport from './pages/reports/PropertiesReport'
import ReceiptsTenantsReport from './pages/reports/ReceiptsTenantsReport'
import ReceiptsOwnersReport from './pages/reports/ReceiptsOwnersReport'
import OverdueTenantsReport from './pages/reports/OverdueTenantsReport'
import OverdueOwnersReport from './pages/reports/OverdueOwnersReport'
import AdvanceTenantsReport from './pages/reports/AdvanceTenantsReport'
import AdvanceOwnersReport from './pages/reports/AdvanceOwnersReport'
import AdjustmentFactorList from './pages/adjustments/AdjustmentFactorList'
import AdjustmentFactorForm from './pages/adjustments/AdjustmentFactorForm'
import AdjustmentFactorDetail from './pages/adjustments/AdjustmentFactorDetail'
import Settings from './pages/settings/Settings'
import AdminGarantias from './pages/admin/AdminGarantias'
import CfePendingList from './pages/dgi/CfePendingList'

function App() {
  const basename = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
  return (
    <AuthProvider>
      <DialogProvider>
        <Router basename={basename}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <UserList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <ClientList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/new"
              element={
                <ProtectedRoute>
                  <ClientForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:id/edit"
              element={
                <ProtectedRoute>
                  <ClientForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:id"
              element={
                <ProtectedRoute>
                  <ClientDetail />
                </ProtectedRoute>
              }
            />
            <Route path="/properties" element={<ProtectedRoute><PropertyList /></ProtectedRoute>} />
            <Route path="/properties/new" element={<ProtectedRoute><PropertyForm /></ProtectedRoute>} />
            <Route path="/properties/:id/edit" element={<ProtectedRoute><PropertyForm /></ProtectedRoute>} />
            <Route path="/properties/:id" element={<ProtectedRoute><PropertyDetail /></ProtectedRoute>} />
            <Route path="/contracts" element={<ProtectedRoute><ContractList /></ProtectedRoute>} />
            <Route path="/contracts/new" element={<ProtectedRoute><ContractForm /></ProtectedRoute>} />
            <Route path="/contracts/:id/edit" element={<ProtectedRoute><ContractForm /></ProtectedRoute>} />
            <Route path="/contracts/:id" element={<ProtectedRoute><ContractDetail /></ProtectedRoute>} />
            <Route path="/payments" element={<ProtectedRoute><PaymentList /></ProtectedRoute>} />
            <Route path="/payments/:id" element={<ProtectedRoute><PaymentDetail /></ProtectedRoute>} />
            <Route path="/receipts" element={<ProtectedRoute><ReciboList /></ProtectedRoute>} />
            <Route path="/receipts/new" element={<ProtectedRoute><ReciboCreate /></ProtectedRoute>} />
            <Route path="/receipts/recent" element={<ProtectedRoute><RecentRecibos /></ProtectedRoute>} />
            <Route path="/receipts/:id" element={<ProtectedRoute><ReciboDetail /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportsDashboard /></ProtectedRoute>} />
            <Route path="/reports/overdue" element={<ProtectedRoute><OverduePaymentsReport /></ProtectedRoute>} />
            <Route path="/reports/adjustments" element={<ProtectedRoute><AdjustmentContractsReport /></ProtectedRoute>} />
            <Route path="/reports/properties-delivered" element={<ProtectedRoute><PropertiesDeliveredReport /></ProtectedRoute>} />
            <Route path="/reports/receipts" element={<ProtectedRoute><ReceiptsReport /></ProtectedRoute>} />
            <Route path="/reports/legal-list" element={<ProtectedRoute><LegalListReport /></ProtectedRoute>} />
            <Route path="/reports/contracts-by-date" element={<ProtectedRoute><ContractsByDateReport /></ProtectedRoute>} />
            <Route path="/reports/contracts-by-month" element={<ProtectedRoute><ContractsByMonthReport /></ProtectedRoute>} />
            <Route path="/reports/cancelled-issued" element={<ProtectedRoute><CancelledIssuedReport /></ProtectedRoute>} />
            <Route path="/reports/clients" element={<ProtectedRoute><ClientsReport /></ProtectedRoute>} />
            <Route path="/reports/properties" element={<ProtectedRoute><PropertiesReport /></ProtectedRoute>} />
            <Route path="/reports/receipts-tenants" element={<ProtectedRoute><ReceiptsTenantsReport /></ProtectedRoute>} />
            <Route path="/reports/receipts-owners" element={<ProtectedRoute><ReceiptsOwnersReport /></ProtectedRoute>} />
            <Route path="/reports/overdue-tenants" element={<ProtectedRoute><OverdueTenantsReport /></ProtectedRoute>} />
            <Route path="/reports/overdue-owners" element={<ProtectedRoute><OverdueOwnersReport /></ProtectedRoute>} />
            <Route path="/reports/advance-tenants" element={<ProtectedRoute><AdvanceTenantsReport /></ProtectedRoute>} />
            <Route path="/reports/advance-owners" element={<ProtectedRoute><AdvanceOwnersReport /></ProtectedRoute>} />
            <Route path="/adjustments" element={<ProtectedRoute><AdjustmentFactorList /></ProtectedRoute>} />
            <Route path="/adjustments/new" element={<ProtectedRoute><AdjustmentFactorForm /></ProtectedRoute>} />
            <Route path="/adjustments/:id" element={<ProtectedRoute><AdjustmentFactorDetail /></ProtectedRoute>} />
            <Route path="/dgi/pending" element={<ProtectedRoute><CfePendingList /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/admin/garantias" element={<ProtectedRoute><AdminGarantias /></ProtectedRoute>} />
          </Routes>
        </Router>
      </DialogProvider>
    </AuthProvider>
  )
}

export default App
